import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { ClientAppEvent } from "constants/index";
import { useWriterComponentState } from "hooks/useWriterComponentState";
import { getApiErrorMessage } from "utils";

import {
  getLaneFormBlockedReason,
  laneFormToRequestBody,
  laneToFormState,
  resolveEditedExpiresAt,
} from "../../utils";
import { LaneFormFields } from "../LaneFormFields";

import type { LaneFormState } from "../../utils";

interface Props {
  lane: ShipmentSearch;
  onClose: VoidFunction;
  onLaneUpdated: VoidFunction;
}

type ExpiryAction = "NONE" | "SET" | "CHANGED" | "UNCHANGED" | "REMOVED";

function getExpiryAction(
  previousExpiresAt: string | null,
  nextExpiresAt: string | null,
): ExpiryAction {
  if (!nextExpiresAt) return previousExpiresAt ? "REMOVED" : "NONE";
  if (!previousExpiresAt) return "SET";
  return nextExpiresAt === previousExpiresAt ? "UNCHANGED" : "CHANGED";
}

export function EditLaneModal(props: Props) {
  const { lane, onClose, onLaneUpdated } = props;
  const posthog = usePostHog();
  const [form, setForm] = useState<LaneFormState>(() => laneToFormState(lane));
  // The hook's own error only reads a top level `message`, so lane writes rejected
  // as DRF field errors are re-extracted here.
  const [writeError, setWriteError] = useState<string | null>(null);

  const { loading, write } = useWriterComponentState({
    errorMessage: "Failed to update lane",
    writeConfig: { method: "put", url: `/lanes/${lane.id}` },
    onWriteFailure: (e) =>
      setWriteError(getApiErrorMessage(e, "Failed to update lane")),
  });

  const handleChange = (patch: Partial<LaneFormState>): void => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const handleSave = (): void => {
    setWriteError(null);
    const body = {
      ...laneFormToRequestBody(form, form.name.trim()),
      expires_at: resolveEditedExpiresAt(form, lane),
    };
    write<ShipmentSearch>(() => {
      posthog?.capture(ClientAppEvent.CARRIER_LANE_EDITED, {
        lane_id: lane.id,
        previous_status: lane.status,
        expiry_action: getExpiryAction(lane.expires_at, body.expires_at),
        expires_at: body.expires_at,
      });
      onLaneUpdated();
      onClose();
    }, body);
  };

  const saveBlockedReason = getLaneFormBlockedReason(form, true);

  return (
    <Dialog
      open
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Edit Lane</DialogTitle>
      <DialogContent sx={{ "&&": { pt: 3 } }}>
        {lane.status === "EXPIRED" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This lane has expired and is not matching new loads. Pick a later
            date to start matching again, or turn the expiry off.
          </Typography>
        )}
        <LaneFormFields value={form} onChange={handleChange} nameRequired />
        {writeError && <ErrorTypography error={writeError} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingWrappedNode loading={loading}>
          <>
            <Tooltip title="Close without saving your changes">
              <Button onClick={onClose}>CANCEL</Button>
            </Tooltip>
            <Tooltip
              title={saveBlockedReason ?? "Save your changes to this lane"}
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={Boolean(saveBlockedReason)}
                >
                  SAVE
                </Button>
              </span>
            </Tooltip>
          </>
        </LoadingWrappedNode>
      </DialogActions>
    </Dialog>
  );
}
